import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  WritableSignal,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { ActivatedRoute, Router } from '@angular/router'

import { Store } from '@ngrx/store'
import { ColDef, GridApi } from 'ag-grid-community'

import { EMPTY } from 'rxjs'
import { catchError, distinctUntilChanged, filter, map, switchMap, tap } from 'rxjs/operators'

import { FlxModalModule, FlxModalService } from '@flowx/angular-ui-toolkit'
import {
  WorkflowInstanceStatus,
  WorkflowInstanceSummary,
} from '@flowx/designer/modules/integration-designer/models/workflow-instance.entity'
import { WorkflowRunTriggerType } from '@flowx/designer/modules/integration-designer/models/workflow.entity'
import { IntegrationDesignerRuntimeApi } from '@flowx/designer/modules/integration-designer/services/integration-designer-runtime.api'
import {
  DefaultActionsColumn,
  FlxResourcesGridComponent,
} from '@flowx/designer/modules/projects/components/resources-grid/resources-grid.component'
import { ActionsCellComponent } from '@flowx/designer/shared/components/ag-grid/cell-components/actions-cell/actions-cell.component'
import { FlxDateCellComponent } from '@flowx/designer/shared/components/ag-grid/cell-components/date-cell.component'
import { FlxInstanceStatusComponent } from '@flowx/designer/shared/components/ag-grid/cell-components/instance-status.component'
import { ConfirmationModalComponent } from '@flowx/designer/shared/components/confirmation-modal/confirmation-modal.component'
import { QueryParamsService } from '@flowx/designer/shared/services/query-params.service'
import { ToastMessageService } from '@flowx/designer/shared/services/toast-message.service'
import { UrlParametersStoreService } from '@flowx/designer/shared/services/url-parameters-store.service'
import { WorkflowInstanceService } from '@flowx/designer/shared/services/workflow-instance.service'
import { selectAppId } from '@flowx/designer/shared/store/selectors/router.selectors'

import { WorkflowInstanceDetailsModalComponent } from './workflow-instance-details-modal/workflow-instance-details-modal.component'

@Component({
  selector: 'flx-instance-workflows',
  imports: [FlxModalModule, WorkflowInstanceDetailsModalComponent, FlxResourcesGridComponent],
  templateUrl: './instance-workflows.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InstanceWorkflowsComponent {
  #workflowInstanceService = inject(WorkflowInstanceService)
  #integrationDesignerRuntimeApi = inject(IntegrationDesignerRuntimeApi)
  #urlParametersStoreService = inject(UrlParametersStoreService)
  #queryParamsService = inject(QueryParamsService)
  #router = inject(Router)
  #route = inject(ActivatedRoute)
  #store = inject(Store)
  #toastMessageService = inject(ToastMessageService)
  #flxModalService = inject(FlxModalService)
  #destroyRef = inject(DestroyRef)
  #gridApi: GridApi

  instanceUuid = input.required<string>()
  workflowRunTriggerTypes = input.required<WorkflowRunTriggerType[]>()

  workflowsTableCols: ColDef[] = [
    { headerName: 'Name', field: 'workflowName' },
    { headerName: 'Status', field: 'status', cellRenderer: FlxInstanceStatusComponent },
    { headerName: 'Date Started', field: 'dateStarted', cellRenderer: FlxDateCellComponent },
    { headerName: 'Date Finished', field: 'dateFinished', cellRenderer: FlxDateCellComponent },
    {
      ...DefaultActionsColumn,
      width: 120,
      cellClass: 'flex items-center justify-end',
      cellRenderer: ActionsCellComponent<WorkflowInstanceSummary>,
      cellRendererParams: (params: { data: WorkflowInstanceSummary }) => ({
        actions: [
          ...(params.data?.status === WorkflowInstanceStatus.STARTED
            ? [
                {
                  icon: 'ph-stop-circle',
                  tooltip: 'Stop workflow',
                  dataTestId: 'stop-workflow',
                  clickAction: (instance: WorkflowInstanceSummary) =>
                    this.stopWorkflowInstance(instance),
                },
              ]
            : []),
          {
            icon: 'ph-eye',
            tooltip: 'View details',
            dataTestId: 'view-details',
            clickAction: (instance: WorkflowInstanceSummary) => this.viewInstanceDetails(instance),
          },
        ],
      }),
    },
  ]
  appId = this.#store.selectSignal(selectAppId)

  loading: WritableSignal<boolean> = signal(false)
  gridOptions = computed(() => ({
    rowModelType: 'infinite',
    datasource: this.#workflowInstanceService.gridDataSource(),
  }))

  showDetailsModal = signal(false)

  constructor() {
    effect(() => {
      const instanceUuid = this.instanceUuid()
      const appId = this.appId()

      if (!instanceUuid || !appId) {
        return
      }

      const workflowRunTriggerTypes = this.workflowRunTriggerTypes()

      this.#workflowInstanceService.setInitialQuery({
        appId,
        workflowRunTriggerTypes,
        triggerUuid: instanceUuid,
      })

      setTimeout(() => {
        this.handleParamsChange()
      }, 100)
    })
  }

  onGridReady(gridApi: GridApi): void {
    this.#gridApi = gridApi
    this.#workflowInstanceService.setGridApi(gridApi)
  }

  handleParamsChange(): void {
    this.#route.queryParams
      .pipe(
        map((params) => this.#queryParamsService.decode(params)?.workflowInstances?.[0]),
        filter((workflowInstance) => !!workflowInstance),
        distinctUntilChanged(),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe(({ workflowInstanceId, workflowName }) => {
        if (!workflowInstanceId || !workflowName) {
          return
        }

        if (workflowInstanceId) {
          this.showDetailsModal.set(true)
        }
      })
  }

  stopWorkflowInstance(instance: WorkflowInstanceSummary): void {
    const { workspaceId } = this.#urlParametersStoreService.urlParameters
    const appId = this.appId()

    if (!workspaceId || !appId) {
      return
    }

    this.#flxModalService
      .show(
        ConfirmationModalComponent,
        {
          closeOnBackdropClick: false,
          backdropClass: 'overlay-backdrop',
        },
        {
          message: 'Are you sure you want to cancel this workflow run?',
          btnOkText: 'Cancel Run',
          btnOkType: 'danger',
        }
      )
      .data$.pipe(
        filter((data) => data.confirm),
        switchMap(() =>
          this.#integrationDesignerRuntimeApi
            .cancelWorkflowInstance(workspaceId, appId, instance.instanceUuid)
            .pipe(
              tap((response) => {
                const rowNode = this.#gridApi?.getRowNode(instance.id)
                rowNode?.setData({
                  ...instance,
                  status: response.status,
                })
              }),
              catchError(() => {
                this.#toastMessageService.showErrorMessage('Failed to stop workflow instance')
                return EMPTY
              })
            )
        ),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe()
  }

  viewInstanceDetails(workflowInstance: WorkflowInstanceSummary): void {
    this.showDetailsModal.set(true)
    this.#router.navigate(
      [
        {
          outlets: {
            workflowInstances: `workflow-instances/${workflowInstance.id}`,
          },
        },
      ],
      {
        relativeTo: this.#route,
        queryParamsHandling: 'merge',
        queryParams: this.#queryParamsService.encode({
          workflowInstances: [
            {
              workflowInstanceId: workflowInstance.id,
              workflowName: workflowInstance.workflowName,
            },
          ],
        }),
      }
    )
  }

  hideDetailsModal(): void {
    this.showDetailsModal.set(false)
    this.#router.navigate(
      [
        {
          outlets: {
            workflowInstances: null,
          },
        },
      ],
      {
        relativeTo: this.#route,
        queryParamsHandling: 'merge',
        queryParams: this.#queryParamsService.encode({ workflowInstances: [] }),
      }
    )
  }
}
